// # Filename: src/components/poker/shared/PokerActionControls.jsx
// Check/Call/Raise/Fold action panel. Self-adapts across breakpoints via its own
// Tailwind responsive classes, so it's mounted unchanged by every tier's table
// component rather than duplicated per breakpoint.

import React from "react";
import { LuRefreshCw, LuCircleDollarSign } from "react-icons/lu";
import { CiHome } from "react-icons/ci";

import Button from "../../ui/Button";
import Tooltip from "../../ui/Tooltip";
import PokerHandStrength from "./PokerHandStrength";

export default function PokerActionControls({
  game,
  raiseTo,
  setRaiseTo,
  raisePanelOpen,
  setRaisePanelOpen,
  raisePresets,
  minRaiseTo,
  maxRaiseTo,
  can,
  canRaiseOrAllIn,
  handleAction,
  onNextHand,
  onPlayAgain,
  navigate,
}) {
  const isCompleted = Boolean(game?.is_completed);
  const isTerminalTableEvent = Boolean(game?.is_terminal_table_event);
  const completedLabel = isTerminalTableEvent
    ? game?.table_event_winner_name
      ? `${game.table_event_winner_name} wins the table`
      : "Table complete"
    : "Next hand auto-deals shortly";

  return (
    <section
      className="relative grid min-w-0 w-full place-items-center overflow-visible sm:gap-3 md:w-[clamp(390px,42vw,540px)] md:rounded-xl md:border md:border-white/[0.08] md:bg-[linear-gradient(180deg,rgba(20,20,22,0.92),rgba(8,8,9,0.97))] md:px-[clamp(8px,0.94vw,12px)] md:py-[clamp(10px,1.25vw,16px)] md:shadow-[0_18px_46px_rgba(0,0,0,0.5)]"
      style={{ gridTemplateAreas: "'stack'" }}
    >
      <div className="pointer-events-none absolute inset-x-4 top-0 hidden h-px bg-gradient-to-r from-transparent via-white/[0.12] to-transparent md:block" />
      {/*
        Both states below are always mounted, stacked in the same named grid area, so the
        panel's own footprint is always the max of the two -- switching between them (e.g.
        hand completes -> next hand deals) never changes the panel's size. That size also
        drives the felt table's dvh reserve (see the desktop table's own sizing effects),
        so letting it change here would visibly resize/jerk the whole table on every hand
        transition.
      */}
      <div
        className="flex w-full flex-col items-center justify-center gap-2 text-center"
        style={{ gridArea: "stack", visibility: isCompleted ? "visible" : "hidden", pointerEvents: isCompleted ? "auto" : "none" }}
        aria-hidden={!isCompleted}
      >
        <div className="text-[clamp(9px,0.86vw,11px)] font-semibold uppercase tracking-[0.18em] text-text-secondary">
          {completedLabel}
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {onNextHand && !isTerminalTableEvent ? (
            <Tooltip content="Deal next hand now">
              <Button type="button" variant="outline" size="sm" onClick={onNextHand}>
                Deal Now
              </Button>
            </Tooltip>
          ) : null}
          {onPlayAgain ? (
            <Tooltip content="New table">
              <Button type="button" variant="secondary" size="sm" onClick={onPlayAgain} aria-label="New table">
                <LuRefreshCw size={16} />
              </Button>
            </Tooltip>
          ) : null}
          <Tooltip content="Home">
            <Button type="button" variant="outline" size="sm" onClick={() => navigate("/")} aria-label="Home">
              <CiHome size={16} />
            </Button>
          </Tooltip>
        </div>
      </div>

      <div
        className="flex w-full min-w-0 flex-col items-center justify-center gap-2 sm:gap-3"
        style={{ gridArea: "stack", visibility: !isCompleted ? "visible" : "hidden", pointerEvents: !isCompleted ? "auto" : "none" }}
        aria-hidden={isCompleted}
      >
        <div className="grid w-full grid-cols-4 gap-1.5 rounded-lg border border-black/40 bg-black/30 p-1 shadow-[inset_0_2px_6px_rgba(0,0,0,0.45),inset_0_-1px_0_rgba(255,255,255,0.03)] sm:flex sm:w-auto sm:flex-wrap sm:items-center sm:justify-center sm:gap-2 sm:p-1.5 md:gap-[clamp(5px,0.63vw,8px)] md:p-[clamp(4px,0.47vw,6px)]">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-11 border border-white/10 bg-[linear-gradient(180deg,rgba(36,38,42,0.7),rgba(15,16,18,0.75))] px-1 text-xs font-semibold text-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] hover:border-white/20 hover:bg-[linear-gradient(180deg,rgba(46,48,52,0.75),rgba(20,21,23,0.8))] sm:h-9 sm:px-4 sm:text-sm md:h-[clamp(30px,2.8vw,36px)] md:px-[clamp(10px,1.25vw,16px)] md:text-[clamp(11px,1.1vw,14px)]"
              disabled={!can("check")}
              onClick={() => handleAction("check")}
            >
              Check
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-11 border border-white/10 bg-[linear-gradient(180deg,rgba(36,38,42,0.7),rgba(15,16,18,0.75))] px-1 text-xs font-semibold text-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] hover:border-white/20 hover:bg-[linear-gradient(180deg,rgba(46,48,52,0.75),rgba(20,21,23,0.8))] sm:h-9 sm:px-4 sm:text-sm md:h-[clamp(30px,2.8vw,36px)] md:px-[clamp(10px,1.25vw,16px)] md:text-[clamp(11px,1.1vw,14px)]"
              disabled={!can("call")}
              onClick={() => handleAction("call")}
            >
              Call {game?.call_amount || ""}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              className="hidden gap-1 border border-emerald-200/25 !bg-emerald-400 px-2 !text-slate-950 shadow-[0_6px_18px_rgba(16,185,129,0.22)] hover:!bg-emerald-300 sm:inline-flex sm:px-4 md:h-[clamp(30px,2.8vw,36px)] md:px-[clamp(10px,1.25vw,16px)] md:text-[clamp(11px,1.1vw,14px)]"
              disabled={!can("raise")}
              onClick={() => handleAction("raise", raiseTo)}
            >
              <LuCircleDollarSign size={14} className="shrink-0" />
              Raise {raiseTo || ""}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              className="h-11 gap-1 border border-emerald-200/25 !bg-emerald-400 px-1 text-xs !text-slate-950 shadow-[0_6px_18px_rgba(16,185,129,0.22)] hover:!bg-emerald-300 sm:hidden"
              disabled={!canRaiseOrAllIn}
              onClick={() => setRaisePanelOpen((open) => !open)}
            >
              <LuCircleDollarSign size={14} className="shrink-0" />
              Bet
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="hidden border-white/12 !bg-[linear-gradient(180deg,rgba(36,38,42,0.7),rgba(15,16,18,0.75))] px-2 !text-slate-100 !shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] hover:border-white/22 sm:inline-flex sm:px-4 md:h-[clamp(30px,2.8vw,36px)] md:px-[clamp(10px,1.25vw,16px)] md:text-[clamp(11px,1.1vw,14px)]"
              disabled={!can("all_in")}
              onClick={() => handleAction("all_in")}
            >
              All In
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-11 border border-white/8 bg-[linear-gradient(180deg,rgba(24,25,28,0.6),rgba(9,10,11,0.65))] px-1 text-xs text-slate-400 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] hover:border-white/16 hover:text-slate-200 sm:h-9 sm:px-4 sm:text-sm md:h-[clamp(30px,2.8vw,36px)] md:px-[clamp(10px,1.25vw,16px)] md:text-[clamp(11px,1.1vw,14px)]"
              disabled={!can("fold")}
              onClick={() => handleAction("fold")}
            >
              Fold
            </Button>
        </div>

        <div className={[
          "w-full max-w-md rounded-lg border border-stone-200/[0.08] bg-[linear-gradient(180deg,rgba(18,15,12,0.84),rgba(8,7,6,0.96))] px-2 py-2 sm:px-3 sm:py-3 md:max-w-[clamp(300px,35vw,448px)] md:px-[clamp(8px,0.94vw,12px)] md:py-[clamp(8px,0.94vw,12px)]",
            raisePanelOpen ? "block" : "hidden sm:block",
            canRaiseOrAllIn ? "opacity-100" : "opacity-45",
          ].join(" ")}>
            <div className="flex min-w-0 items-center justify-between gap-3">
              <PokerHandStrength hand={game?.my_current_hand} compact />
              <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[clamp(8px,0.78vw,10px)] font-bold uppercase tracking-[0.16em] text-slate-100">
                <span className="text-text-muted">Raise To</span>
                {raiseTo || "-"}
              </span>
            </div>
            <div className="mt-2 rounded-full bg-black/40 px-2 py-1.5 shadow-[inset_0_2px_4px_rgba(0,0,0,0.5)]">
              <input
                type="range"
                min={minRaiseTo || 0}
                max={maxRaiseTo || minRaiseTo || 0}
                step="10"
                value={raiseTo || minRaiseTo || 0}
                disabled={!can("raise")}
                onChange={(e) => setRaiseTo(Number(e.target.value))}
                className="w-full accent-emerald-400"
              />
            </div>
            <div className="mt-2 grid grid-cols-4 justify-items-center gap-1.5 sm:flex sm:flex-wrap sm:justify-center">
              {raisePresets.map((amount) => {
                const isSelected = raiseTo === amount;
                return (
                  <button
                    key={amount}
                    type="button"
                    disabled={!can("raise")}
                    onClick={() => setRaiseTo(amount)}
                    className={[
                      "relative grid h-9 w-9 shrink-0 place-items-center rounded-full border text-[9px] font-black leading-none transition disabled:opacity-40 md:h-[clamp(27px,2.7vw,34px)] md:w-[clamp(27px,2.7vw,34px)] md:text-[clamp(7px,0.78vw,9px)]",
                      isSelected
                        ? "border-emerald-200/40 bg-emerald-400 text-slate-950 shadow-[0_0_10px_rgba(16,185,129,0.4)]"
                        : "border-white/10 bg-white/5 text-slate-300 hover:border-white/20 hover:bg-white/10",
                    ].join(" ")}
                  >
                    {amount === maxRaiseTo ? "MAX" : amount}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-1.5 sm:hidden">
              <Button
                type="button"
                variant="primary"
                size="sm"
                className="h-11 gap-1 border border-emerald-200/25 !bg-emerald-400 text-xs !text-slate-950 shadow-[0_6px_18px_rgba(16,185,129,0.22)] hover:!bg-emerald-300"
                disabled={!can("raise")}
                onClick={() => handleAction("raise", raiseTo)}
              >
                <LuCircleDollarSign size={14} className="shrink-0" />
                Raise {raiseTo || ""}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-11 border-white/12 !bg-[linear-gradient(180deg,rgba(36,38,42,0.7),rgba(15,16,18,0.75))] text-xs !text-slate-100 !shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] hover:border-white/22"
                disabled={!can("all_in")}
                onClick={() => handleAction("all_in")}
              >
                All In
              </Button>
            </div>
        </div>
      </div>
    </section>
  );
}
